import { createContext, useContext, useState, useCallback } from "react";

const translations = {
  fr: {
    // Header
    anonymous: "ANONYME",
    login: "Se connecter",
    lightMode: "Mode clair",
    darkMode: "Mode sombre",

    // EmailGenerator
    tempAddress: "Adresse Temporaire",
    permAddress: "Adresse Permanente",
    customPlaceholder: "mon-adresse",
    customLabel: "Adresse personnalisée (optionnel)",
    customizeAddress: "Personnaliser l'adresse",
    copy: "Copier",
    newEmail: "Nouveau",
    delete: "Supprimer",
    duration: "Durée:",
    domain: "Domaine:",
    generateAddress: "Générer une adresse",

    // TimerWidget
    autoDestruction: "Auto-Destruction",
    permanent: "PERMANENT",
    neverExpires: "Cet email n'expire jamais",
    expirationWarning: "ATTENTION: Expiration imminente",
    timeRemaining: "Temps restant avant expiration",

    // InboxSection
    inbox: "Boîte de réception",
    sender: "Expéditeur",
    subject: "Sujet",
    markRead: "Marquer comme lu",
    read: "Lu",
    markUnread: "Marquer comme non lu",
    unread: "Non lu",
    deleteCount: (n) => `Supprimer (${n})`,
    refresh: "Actualiser",
    searchPlaceholder: "Rechercher par expéditeur ou sujet...",
    deselectAll: "Tout désélectionner",
    selectAll: "Tout sélectionner",
    noResults: (q) => `Aucun résultat pour "${q}"`,
    clearSearch: "Effacer la recherche",
    sent: "Envoyé",
    sentTo: (r) => `→ ${r}`,
    waitingTransmission: "En attente de transmission...",
    messagesWillAppear: "Les messages apparaîtront ici",

    // AddressHistory
    addressHistory: "Historique des adresses",
    clearHistory: "Effacer l'historique",
    justNow: "À l'instant",
    minutesAgo: (n) => `Il y a ${n}min`,
    hoursAgo: (n) => `Il y a ${n}h`,
    daysAgo: (n) => `Il y a ${n}j`,

    // MessageModal
    messageFrom: (s) => `Message reçu de ${s}`,
    from: "De:",
    received: "Reçu:",
    replyTo: "Répondre à",
    replyPlaceholder: "Votre réponse...",
    send: "Envoyer",
    reply: "Répondre",
    deleteMessage: "Supprimer",
    deleteMessageConfirm: "Supprimer ce message ?",
    previousMessage: "Message précédent",
    nextMessage: "Message suivant",
    messageOf: (current, total) => `${current} / ${total}`,

    // PullToRefresh
    pullDone: "Terminé !",
    pullRefreshing: "Actualisation...",
    pullRelease: "Relâcher",
    pullToRefresh: "Tirer pour actualiser",

    // InstallPrompt
    installTitle: "Installer ShadowMail",
    installDesc: "Accédez plus rapidement à vos mails temporaires",
    installSubtitle: "Installez l'application pour un accès rapide",
    install: "Installer",
    installHow: "Comment installer ?",
    installClose: "Fermer",
    installStep: "Étape",
    // iOS Safari
    iosSafariStep1: "Appuyez sur les trois points ···",
    iosSafariStep2: "Appuyez sur le bouton de partage",
    iosSafariStep3: "Faites défiler et appuyez sur",
    iosSafariStep4: "\"Sur l'écran d'accueil\"",
    // iOS Chrome
    iosChromeStep1: "Appuyez sur les trois points ···",
    iosChromeStep2: "Appuyez sur \"Partager...\"",
    iosChromeStep3: "Appuyez sur \"Sur l'écran d'accueil\"",
    // Android Chrome
    androidStep1: "Appuyez sur les trois points ⋮",
    androidStep2: "Appuyez sur \"Ajouter à l'écran d'accueil\"",
    androidStep3: "Confirmez en appuyant sur \"Ajouter\"",
    // Desktop Chrome/Edge
    desktopChromeStep1: "Cliquez sur l'icône d'installation dans la barre d'adresse",
    desktopChromeStep2: "Ou appuyez sur ⋮ → \"Installer ShadowMail\"",
    desktopChromeStep3: "Cliquez sur \"Installer\" pour confirmer",
    // Desktop Firefox
    desktopFirefoxInfo: "Firefox ne supporte pas l'installation d'applications. Utilisez Chrome ou Edge pour installer ShadowMail.",
    // Desktop Safari
    desktopSafariInfo: "Sur macOS, utilisez Safari 17+ : Fichier → Ajouter au Dock",

    // MaintenancePage
    maintenanceTitle: "Maintenance en cours",
    maintenanceDesc: "Le site est temporairement indisponible pour maintenance.",
    maintenanceBack: "Nous serons de retour très bientôt.",

    // NotFoundPage
    routeNotFound: "Route introuvable",
    pageNotExists: "La page demandée n'existe pas ou a été supprimée.",
    terminal: "terminal",
    backHome: "Retour à l'accueil",

    // OfflineIndicator
    offline: "Vous êtes hors-ligne — certaines fonctionnalités sont indisponibles",

    // UpdateNotification
    updateAvailable: "Nouvelle version disponible — Mettre à jour",

    // Toast messages
    toastCopied: "Copié!",
    toastCopiedDesc: "Adresse copiée dans le presse-papier",
    toastCopyError: "Erreur lors de la copie",
    toastNewAddress: "Nouvelle adresse générée",
    toastAddressInUse: "Cette adresse est déjà utilisée",
    toastInvalidAddress: "Adresse invalide",
    toastGenerateError: "Erreur lors de la génération",
    toastOverloaded: "Le service est temporairement surchargé. Réessayez dans quelques secondes.",
    toastExpired: "Email expiré",
    toastExpiredDesc: "Générez une nouvelle adresse",
    toastInboxRefreshed: "Boîte actualisée",
    toastNoNewMessage: "Aucun nouveau message",
    toastRefreshError: "Erreur lors de l'actualisation",
    toastDeleted: (n) => `${n} message(s) supprimé(s)`,
    toastDeleteError: "Erreur lors de la suppression",
    toastMarkedRead: "Marqué(s) comme lu(s)",
    toastMarkedUnread: "Marqué(s) comme non lu(s)",
    toastUpdateError: "Erreur lors de la mise à jour",
    toastLoadError: "Erreur lors du chargement du message",
    toastEmailDeleted: "Email supprimé",
    toastReplySent: "Réponse envoyée",
    toastReplyError: "Échec de l'envoi",
    toastUnknownError: "Erreur inconnue",
    notifNewMessage: "Nouveau message",
    notifFrom: (s, subj) => `De: ${s}\n${subj}`,
    messages: (n) => `${n} message(s)`,

    // Lifetime options
    lifetime10min: "10 minutes",
    lifetime1h: "1 heure",
    lifetime24h: "24 heures",
    lifetimePermanent: "Permanent",

    // Footer
    allRightsReserved: "Tous droits réservés",
    footerDefault: "Vos données sont automatiquement supprimées après expiration. Aucune collecte personnelle.",

    // Page title
    pageTitle: (name) => `${name} - Mail Temporaire Anonyme et Gratuit`,

    dateLocale: "fr-FR",
  },

  en: {
    // Header
    anonymous: "ANONYMOUS",
    login: "Sign in",
    lightMode: "Light mode",
    darkMode: "Dark mode",

    // EmailGenerator
    tempAddress: "Temporary Address",
    permAddress: "Permanent Address",
    customPlaceholder: "my-address",
    customLabel: "Custom address (optional)",
    customizeAddress: "Customize address",
    copy: "Copy",
    newEmail: "New",
    delete: "Delete",
    duration: "Duration:",
    domain: "Domain:",
    generateAddress: "Generate an address",

    // TimerWidget
    autoDestruction: "Auto-Destruction",
    permanent: "PERMANENT",
    neverExpires: "This email never expires",
    expirationWarning: "WARNING: Expiration imminent",
    timeRemaining: "Time remaining before expiration",

    // InboxSection
    inbox: "Inbox",
    sender: "Sender",
    subject: "Subject",
    markRead: "Mark as read",
    read: "Read",
    markUnread: "Mark as unread",
    unread: "Unread",
    deleteCount: (n) => `Delete (${n})`,
    refresh: "Refresh",
    searchPlaceholder: "Search by sender or subject...",
    deselectAll: "Deselect all",
    selectAll: "Select all",
    noResults: (q) => `No results for "${q}"`,
    clearSearch: "Clear search",
    sent: "Sent",
    sentTo: (r) => `→ ${r}`,
    waitingTransmission: "Waiting for messages...",
    messagesWillAppear: "Messages will appear here",

    // AddressHistory
    addressHistory: "Address history",
    clearHistory: "Clear history",
    justNow: "Just now",
    minutesAgo: (n) => `${n}min ago`,
    hoursAgo: (n) => `${n}h ago`,
    daysAgo: (n) => `${n}d ago`,

    // MessageModal
    messageFrom: (s) => `Message received from ${s}`,
    from: "From:",
    received: "Received:",
    replyTo: "Reply to",
    replyPlaceholder: "Your reply...",
    send: "Send",
    reply: "Reply",
    deleteMessage: "Delete",
    deleteMessageConfirm: "Delete this message?",
    previousMessage: "Previous message",
    nextMessage: "Next message",
    messageOf: (current, total) => `${current} / ${total}`,

    // PullToRefresh
    pullDone: "Done!",
    pullRefreshing: "Refreshing...",
    pullRelease: "Release",
    pullToRefresh: "Pull to refresh",

    // InstallPrompt
    installTitle: "Install ShadowMail",
    installDesc: "Access your temporary emails faster",
    installSubtitle: "Install the app for quick access",
    install: "Install",
    installHow: "How to install?",
    installClose: "Close",
    installStep: "Step",
    iosSafariStep1: "Tap the three dots ···",
    iosSafariStep2: "Tap the Share button",
    iosSafariStep3: "Scroll down and tap",
    iosSafariStep4: "\"Add to Home Screen\"",
    iosChromeStep1: "Tap the three dots ···",
    iosChromeStep2: "Tap \"Share...\"",
    iosChromeStep3: "Tap \"Add to Home Screen\"",
    androidStep1: "Tap the three dots ⋮",
    androidStep2: "Tap \"Add to Home Screen\"",
    androidStep3: "Confirm by tapping \"Add\"",
    desktopChromeStep1: "Click the install icon in the address bar",
    desktopChromeStep2: "Or click ⋮ → \"Install ShadowMail\"",
    desktopChromeStep3: "Click \"Install\" to confirm",
    desktopFirefoxInfo: "Firefox doesn't support app installation. Use Chrome or Edge to install ShadowMail.",
    desktopSafariInfo: "On macOS, use Safari 17+: File → Add to Dock",

    // MaintenancePage
    maintenanceTitle: "Maintenance in progress",
    maintenanceDesc: "The site is temporarily unavailable for maintenance.",
    maintenanceBack: "We'll be back very soon.",

    // NotFoundPage
    routeNotFound: "Route not found",
    pageNotExists: "The requested page does not exist or has been removed.",
    terminal: "terminal",
    backHome: "Back to home",

    // OfflineIndicator
    offline: "You are offline — some features are unavailable",

    // UpdateNotification
    updateAvailable: "New version available — Update now",

    // Toast messages
    toastCopied: "Copied!",
    toastCopiedDesc: "Address copied to clipboard",
    toastCopyError: "Copy failed",
    toastNewAddress: "New address generated",
    toastAddressInUse: "This address is already in use",
    toastInvalidAddress: "Invalid address",
    toastGenerateError: "Generation failed",
    toastOverloaded: "Service temporarily overloaded. Try again in a few seconds.",
    toastExpired: "Email expired",
    toastExpiredDesc: "Generate a new address",
    toastInboxRefreshed: "Inbox refreshed",
    toastNoNewMessage: "No new messages",
    toastRefreshError: "Refresh failed",
    toastDeleted: (n) => `${n} message(s) deleted`,
    toastDeleteError: "Deletion failed",
    toastMarkedRead: "Marked as read",
    toastMarkedUnread: "Marked as unread",
    toastUpdateError: "Update failed",
    toastLoadError: "Failed to load message",
    toastEmailDeleted: "Email deleted",
    toastReplySent: "Reply sent",
    toastReplyError: "Send failed",
    toastUnknownError: "Unknown error",
    notifNewMessage: "New message",
    notifFrom: (s, subj) => `From: ${s}\n${subj}`,
    messages: (n) => `${n} message(s)`,

    // Lifetime options
    lifetime10min: "10 minutes",
    lifetime1h: "1 hour",
    lifetime24h: "24 hours",
    lifetimePermanent: "Permanent",

    // Footer
    allRightsReserved: "All rights reserved",
    footerDefault: "Your data is automatically deleted after expiration. No personal data collected.",

    // Page title
    pageTitle: (name) => `${name} - Free Anonymous Temporary Email`,

    dateLocale: "en-US",
  },
};

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('shadowmail_lang') || 'fr');

  const setLanguage = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('shadowmail_lang', newLang);
    document.documentElement.setAttribute('lang', newLang);
  }, []);

  const t = translations[lang] || translations.fr;

  return (
    <I18nContext.Provider value={{ lang, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for usage outside provider
    return { lang: 'fr', setLanguage: () => {}, t: translations.fr };
  }
  return ctx;
}

export { translations };
